// The header pickers of a compare's and a history's page (navigation spec,
// piece 5): the base, the head and the ref become pickers over the jump
// bar's completion (jump/complete.ts `Completer`, the branches source and
// its JUMP_REFS read, the rows), scoped to the repository, and for a head
// to its forks (`ana:feat`). Every address a choice leads to is the
// router's own (`comparePath`, `subjectPath`, read back by
// `parseSubjectPath`), so a name the list does not have is taken as the
// router reads it, and one it refuses is offered nowhere.
import { carryFilter } from '../logic/filefilter';
import { comparePath, parseSubjectPath, subjectPath } from '../router';
import { compareHead, isGitHubName, type CompareSubject, type RefSubject } from '../subject';
import type { JumpQuery, Source } from './complete';
import { branchId, type Row } from './rows';
import { branchSource, type BranchScope, type Branches } from './sources';

/** Which name a picker changes: a compare's base or head, or a history's ref. */
export type PickEnd = 'base' | 'head' | 'ref';

/** Where a picker is: the subject, the end it picks, and what the page carries to the next (its view, the Files filter in its query). */
export interface PickAt {
  subject: CompareSubject | RefSubject;
  end: PickEnd;
  view: 'files' | 'commits';
  query: URLSearchParams;
  /** The repository's default branch, once known: a compare's base when its URL names none. */
  defaultBranch: string | null;
}

/** What choosing a name does, as its row says. */
export const PICK_ACTION: Readonly<Record<PickEnd, string>> = { base: 'Use as base', head: 'Use as head', ref: 'Show its history' };

/** A compare's path (`comparePath`) on `view`, with the Files filter of `query`. */
function compareOn(owner: string, repo: string, base: string, head: string, view: PickAt['view'], query: URLSearchParams): string | null {
  const href = comparePath(owner, repo, base, head);
  const at = href === null ? null : parseSubjectPath(href);
  return at && 'subject' in at && at.subject.kind === 'compare' ? carryFilter(subjectPath(at.subject, view), query) : null;
}

/**
 * Where choosing `name` leads: the compare with it as its base (the head
 * kept, a fork's too) or as its head (`feat`, `ana:feat`, `ana:fork:feat`;
 * the base kept, or none for the default branch), or the history of it
 * (the path kept apart, `?under=`); on the page's view, with its Files
 * filter, and nothing of the old range. Null when the router reads no such
 * subject (an empty name, `main..x`, `x~1`, a fork's branch as a base), and
 * for the other end's own name (a compare of a branch with itself is empty).
 */
export function pickedPath(at: PickAt, name: string): string | null {
  const n = name.trim();
  if (!n) return null;
  const s = at.subject;
  if (s.kind === 'compare') {
    if (at.end === 'ref') return null;
    // A base is in this repository: `:` is a head's fork, and no name git takes.
    if (otherEnd(at) === n) return null;
    if (at.end === 'base') return n.includes(':') ? null : compareOn(s.owner, s.repo, n, compareHead(s), at.view, at.query);
    return compareOn(s.owner, s.repo, s.base ?? '', n, at.view, at.query);
  }
  if (at.end !== 'ref') return null;
  // The router reads the URL back as this ref whole (and the path apart), or the name is none it takes.
  const href = subjectPath({ ...s, ref: n }, at.view);
  const url = new URL(href, 'http://x');
  const read = parseSubjectPath(url.pathname, url.search);
  if (!read || !('locator' in read) || read.locator.spec !== n || (read.locator.path ?? null) !== s.path) return null;
  return carryFilter(href, at.query);
}

/**
 * The compare the other way round (`base` the compare's base by name, the
 * default branch's when its URL names none), on `view` with the Files
 * filter; null for a fork's head (a base is in this repository) or a
 * default branch not yet known.
 */
export function swapPath(s: CompareSubject, base: string | null, view: PickAt['view'], query: URLSearchParams): string | null {
  const b = s.base ?? base;
  if (s.headRepo || !b) return null;
  return compareOn(s.owner, s.repo, s.head, b, view, query);
}

/** A compare's other end, as the field writes a name: what this end cannot be. Null for a history, or a base not yet known. */
function otherEnd(at: PickAt): string | null {
  const s = at.subject;
  if (s.kind !== 'compare') return null;
  return at.end === 'base' ? compareHead(s) : (s.base ?? at.defaultBranch);
}

/** A filtered history's breadcrumb: the whole history, then each folder on the way to the path, the path itself last. */
export interface PathTrail {
  whole: string;
  parts: { name: string; href: string; current: boolean }[];
}

/** The breadcrumb of a history filtered to a path, each part narrowing to it, on `view` with the Files filter; null for the whole history. */
export function pathTrail(s: RefSubject, view: PickAt['view'], query: URLSearchParams): PathTrail | null {
  if (s.path === null) return null;
  const names = s.path.split('/');
  return {
    whole: carryFilter(subjectPath({ ...s, path: null }, view), query),
    parts: names.map((name, i) => ({ name, href: carryFilter(subjectPath({ ...s, path: names.slice(0, i + 1).join('/') }, view), query), current: i === names.length - 1 })),
  };
}

/** A head's fork as the field writes it before a branch name: `ana:`, `ana:fork:`. */
const forkPrefix = (s: CompareSubject) => (s.headRepo ? `${s.headRepo.owner}:${s.headRepo.repo === null ? '' : s.headRepo.repo + ':'}` : '');

/** What the field holds as it opens: a fork's head's fork (so its branches are listed), else nothing. */
export function pickInitial(at: PickAt): string {
  return at.end === 'head' && at.subject.kind === 'compare' ? forkPrefix(at.subject) : '';
}

/**
 * Where the text's branches are read: the repository; for a head, a fork
 * the text names (`ana:` is ana's fork by the repository's name, as GitHub
 * names a fork unless renamed; `ana:fork:` names it).
 */
function scopeOf(at: PickAt, text: string): BranchScope {
  const s = at.subject;
  const t = text.trim();
  if (at.end === 'head') {
    const named = /^([^:]+):([^:]+):(.*)$/.exec(t) ?? /^([^:]+):()(.*)$/.exec(t);
    if (named && isGitHubName(named[1]) && (!named[2] || isGitHubName(named[2]))) {
      const [, owner, repo, prefix] = named;
      return { repo: { owner, repo: repo || s.repo }, prefix, written: (name) => `${owner}:${repo ? repo + ':' : ''}${name}` };
    }
  }
  return { repo: { owner: s.owner, repo: s.repo }, prefix: t, written: (name) => name };
}

/** The name the picker's end has now, as the field writes it. */
function currentOf(at: PickAt): string | null {
  const s = at.subject;
  if (s.kind === 'ref') return s.ref;
  return at.end === 'base' ? (s.base ?? at.defaultBranch) : compareHead(s);
}

export interface PickOptions {
  /** A repository's branches matching a prefix (jump/github.ts `readBranches`, JUMP_REFS); null: GitHub shows no such repository. */
  read: (repo: { owner: string; repo: string }, prefix: string, signal: AbortSignal) => Promise<Branches | null>;
  debounceMs?: number;
}

/** A picker's completion: its sources, for a `Completer`, and why a text lists nothing. */
export interface PickCompletion {
  sources: Source[];
  /**
   * Why the list holds no branch for `text` (asked once the sources have
   * answered): the fork it names is not visible, a base is in this
   * repository, it is the other end, the router reads no name in it, or no
   * branch matches (Enter takes it as typed); empty when there is nothing
   * to say.
   */
  why(text: string, branches: number): string;
}

const repoKey = (r: { owner: string; repo: string }) => `${r.owner}/${r.repo}`.toLowerCase();

/**
 * A picker's completion. Its sources: what is typed, as the router reads
 * it (a guess the branches' row of the same name replaces, listed last "As
 * typed" once they answered); and the branches where the text points, the
 * latest committed to before anything is typed, each one choice, the
 * other end of a compare never among them.
 */
export function pickCompletion(at: () => PickAt, o: PickOptions): PickCompletion {
  /** The repositories GitHub answered it shows none of (a fork named that it will not show). */
  const unseen = new Set<string>();
  const read: PickOptions['read'] = async (repo, prefix, signal) => {
    const b = await o.read(repo, prefix, signal);
    if (b) unseen.delete(repoKey(repo));
    else unseen.add(repoKey(repo));
    return b;
  };
  const typed: Source = {
    id: 'typed',
    title: 'As typed',
    rows: (q: JumpQuery): Row[] => {
      const p = at();
      const href = pickedPath(p, q.text);
      if (!href) return [];
      const scope = scopeOf(p, q.text);
      return [{ id: branchId(scope.repo, scope.prefix), title: q.text.trim(), detail: 'as typed', actions: [{ kind: 'go', label: PICK_ACTION[p.end], href }], provisional: true }];
    },
  };
  const branches = branchSource(read, {
    empty: true,
    ...(o.debounceMs === undefined ? {} : { debounceMs: o.debounceMs }),
    scope: (q) => scopeOf(at(), q.text),
    row: (scope, b, defaultBranch) => {
      const p = at();
      const name = scope.written(b.name);
      const href = pickedPath(p, name);
      if (!href) return null;
      const here = repoKey(scope.repo) === repoKey(p.subject);
      const detail = [name === currentOf(p) ? 'current' : null, b.pull ? `#${b.pull.number} ${b.pull.title}` : null, here && b.name === defaultBranch ? 'default branch' : null].filter((x) => x !== null).join(' · ');
      return { id: branchId(scope.repo, b.name), title: name, ...(detail ? { detail } : {}), actions: [{ kind: 'go', label: PICK_ACTION[p.end], href }] };
    },
  });
  return {
    sources: [typed, branches],
    why(text, listed) {
      const p = at();
      const t = text.trim();
      if (!t) return listed ? '' : 'No branches';
      const scope = scopeOf(p, t);
      if (repoKey(scope.repo) !== repoKey(p.subject) && unseen.has(repoKey(scope.repo))) {
        const named = /^([^:]+):(?:([^:]+):)?/.exec(t)!;
        return `No fork ${named[1]}${named[2] ? '/' + named[2] : ''} of this repository is visible to Changeling`;
      }
      if (p.subject.kind === 'compare' && p.end === 'base' && t.includes(':')) return 'A base is a branch of this repository: a fork’s branch (ana:feat) can only be the head';
      if (otherEnd(p) === t) return p.end === 'base' ? `${t} is the head: a compare of it with itself is empty` : `${t} is the base: a compare of it with itself is empty`;
      if (!pickedPath(p, t)) return 'No branch, tag or commit can be named that';
      return listed ? '' : `No branch holds ${t}: Enter takes it as typed`;
    },
  };
}
